import erpCatalogsApi from 'dashboard/api/erpCatalogs';

const state = {
  catalogsByAccount: {},
  uiFlags: {
    isLoading: false,
    isSaving: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCatalogs: _state => accountId => {
    const key = String(accountId || '');
    return _state.catalogsByAccount[key] || [];
  },
  getCatalogById: _state => (accountId, catalogId) => {
    const catalogs = getters.getCatalogs(_state)(accountId);
    return catalogs.find(c => String(c.id) === String(catalogId));
  },
};

export const mutations = {
  SET_UI_FLAGS(_state, flags = {}) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  SET_CATALOGS(_state, { accountId, catalogs }) {
    const key = String(accountId || '');
    _state.catalogsByAccount = { ..._state.catalogsByAccount, [key]: catalogs };
  },
  UPSERT_CATALOG(_state, { accountId, catalog }) {
    const key = String(accountId || '');
    const current = _state.catalogsByAccount[key] || [];
    const next = [...current];
    const idx = next.findIndex(c => String(c.id) === String(catalog.id));
    if (idx === -1) {
      next.unshift(catalog);
    } else {
      next[idx] = catalog;
    }
    _state.catalogsByAccount = { ..._state.catalogsByAccount, [key]: next };
  },
};

export const actions = {
  load: async ({ commit }, { accountId }) => {
    commit('SET_UI_FLAGS', { isLoading: true });
    const key = String(accountId || '');
    const resp = await erpCatalogsApi.listCatalogs();
    const catalogs = resp?.data?.data || [];
    commit('SET_CATALOGS', { accountId: key, catalogs });
    commit('SET_UI_FLAGS', { isLoading: false });
  },

  createCatalog: async ({ commit, dispatch }, { accountId, payload }) => {
    commit('SET_UI_FLAGS', { isSaving: true });
    const key = String(accountId || '');
    const resp = await erpCatalogsApi.createCatalog(payload);
    const catalog = resp?.data?.data;
    if (catalog) commit('UPSERT_CATALOG', { accountId: key, catalog });
    await dispatch('load', { accountId: key });
    commit('SET_UI_FLAGS', { isSaving: false });
    return catalog;
  },

  updateCatalog: async ({ commit }, { accountId, catalogId, payload }) => {
    commit('SET_UI_FLAGS', { isSaving: true });
    const key = String(accountId || '');
    const resp = await erpCatalogsApi.updateCatalog(catalogId, payload);
    const catalog = resp?.data?.data;
    if (catalog) commit('UPSERT_CATALOG', { accountId: key, catalog });
    commit('SET_UI_FLAGS', { isSaving: false });
    return catalog;
  },

  reconnectCatalog: async ({ commit, dispatch }, { accountId, catalogId }) => {
    commit('SET_UI_FLAGS', { isSaving: true });
    const key = String(accountId || '');
    const resp = await erpCatalogsApi.reconnectCatalog(catalogId);
    const catalog = resp?.data?.data;
    if (catalog) commit('UPSERT_CATALOG', { accountId: key, catalog });
    await dispatch('load', { accountId: key });
    commit('SET_UI_FLAGS', { isSaving: false });
    return catalog;
  },

  reorderCatalogs: async ({ state: _state, commit, dispatch }, { accountId, ids }) => {
    const key = String(accountId || '');
    const existing = _state.catalogsByAccount[key] || [];
    const idsNormalized = Array(ids)
      .flatMap(v => String(v).split(',')) // fallback se vier "1,2,3"
      .map(v => v.trim())
      .filter(v => v && /^\d+$/.test(v));
    if (idsNormalized.length === 0) return;

    const byId = existing.reduce((acc, c) => {
      acc[String(c.id)] = c;
      return acc;
    }, {});

    const next = idsNormalized
      .map(id => byId[id])
      .filter(Boolean)
      .map((c, idx) => ({ ...c, position: idx + 1 }));

    // Atualiza imediatamente no UI (sidebar + lista), e depois persiste.
    commit('SET_CATALOGS', { accountId: key, catalogs: next });

    commit('SET_UI_FLAGS', { isSaving: true });
    try {
      const resp = await erpCatalogsApi.reorderCatalogs({ ids: idsNormalized });
      const catalogs = resp?.data?.data || [];
      if (catalogs.length) {
        commit('SET_CATALOGS', { accountId: key, catalogs });
      }
    } catch (e) {
      // rollback + reload para garantir consistência
      commit('SET_CATALOGS', { accountId: key, catalogs: existing });
      await dispatch('load', { accountId: key });
      throw e;
    } finally {
      commit('SET_UI_FLAGS', { isSaving: false });
    }
  },

  deleteCatalog: async ({ commit, dispatch }, { accountId, catalogId }) => {
    commit('SET_UI_FLAGS', { isSaving: true });
    await erpCatalogsApi.deleteCatalog(catalogId);
    await dispatch('load', { accountId: String(accountId || '') });
    commit('SET_UI_FLAGS', { isSaving: false });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

